import React, { useEffect, useState } from "react";
import { Flame, TrendingUp, MessageSquare, Handshake, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis } from "recharts";

export default function StreakWidget() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/streak/mine");
        setData(data);
      } catch (e) {
        console.warn("streak_load_failed", e?.response?.status || e?.message);
      }
    })();
  }, []);

  if (!data) return null;

  const items = [
    { label: t("dashboard.days_active"), value: data.streak_days, icon: Flame, tone: "coral" },
    { label: t("dashboard.projects_this_week"), value: data.published_week + data.briefs_week, icon: TrendingUp },
    { label: t("dashboard.messages_this_week"), value: data.messages_week, icon: MessageSquare },
    { label: t("dashboard.active_rooms"), value: data.active_rooms, icon: Handshake },
    { label: t("dashboard.milestones_completed"), value: data.milestones_completed, icon: CheckCircle2 },
  ];

  return (
    <div className="kpi-card p-6 mb-8" data-testid="streak-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="eyebrow text-gold">{t("dashboard.rhythm_title")}</p>
          <h3 className="section-title mt-1">{t("dashboard.recent_activity")}</h3>
        </div>
        {data.streak_days >= 3 && (
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-coral to-amber-500 text-white rounded-full px-4 py-1.5 text-sm font-semibold">
            <Flame size={14} /> {t("dashboard.streak_days_badge", { count: data.streak_days })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {items.map((it, i) => {
          const Icon = it.icon;
          return (
            <div key={i} className="border border-slate-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-1">
                <span className={`inline-flex h-7 w-7 rounded-full items-center justify-center ${it.tone === "coral" ? "bg-coral/10 text-coral" : "bg-gold/10 text-gold"}`}>
                  <Icon size={13} />
                </span>
                <span className="text-2xl font-display text-petroleum">{it.value}</span>
              </div>
              <p className="text-[11px] text-slate-500 leading-tight">{it.label}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <p className="text-xs text-slate-500 mb-2">{t("dashboard.last_15_days")}</p>
        <ResponsiveContainer width="100%" height={64}>
          <BarChart data={data.daily} margin={{ top: 4, bottom: 0, left: 0, right: 0 }}>
            <XAxis dataKey="day" tick={{ fontSize: 9, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={1} />
            <Tooltip
              contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 11 }}
              labelStyle={{ color: "#0f172a" }}
              cursor={false}
            />
            <Bar dataKey="count" fill="#D4AF37" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
